import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorMessage from './common/ErrorMessage';
import { apiGet, apiPut, apiPost, apiDelete } from '../utils/api';
import { getLevelDisplayName, getStatusDisplayName } from '../utils/formatters';
import { useDataFetch } from '../hooks/useApi';
import { useUrlState } from '../hooks/useUrlState';
import PayrollRatesManager from './payroll/PayrollRatesManager';
import './AdminPanel.css';

function AdminPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // 고른 탭은 주소에 둔다(?tab=users|permissions|notices|payroll-rates). 탭을 바꾸면 그 탭 안의 선택은 지운다
  const [url, setUrl] = useUrlState();
  const activeTab = url.get('tab') || 'users';
  const setActiveTab = (tab) => setUrl({ tab: tab === 'users' ? null : tab, company: null, rate: null, rate_month: null });
  const [message, setMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newLevel, setNewLevel] = useState('user');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  
  // 사용자 생성 폼 상태
  const [newUser, setNewUser] = useState({
    id: '',
    name: '',
    password: '',
    level: '일반'
  });

  // 사용자 목록 페칭
  const { 
    data: users, 
    loading, 
    error, 
    refetch: refetchUsers 
  } = useDataFetch(() => apiGet('/api/auth/users'), []);

  const handleLevelChange = async (id, newLevel) => {
    try {
      const data = await apiPut('/api/auth/users/level', {
        id: id,
        new_level: newLevel
      });

      setMessage(data.message);
      refetchUsers(); // 사용자 목록 새로고침
      setSelectedUser(null);
    } catch (error) {
      setMessage(error.message || '레벨 변경에 실패했습니다.');
    }
  };

  const handleResetPassword = async (id) => {
    if (!window.confirm(`${id}의 비밀번호를 1234로 초기화하시겠습니까?`)) {
      return;
    }

    try {
      const data = await apiPost('/api/auth/users/reset-password', {
        id: id
      });

      setMessage(data.message);
      refetchUsers();
    } catch (error) {
      setMessage(error.message || '비밀번호 초기화에 실패했습니다.');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.id || !newUser.name || !newUser.password) {
      setMessage('아이디, 이름, 비밀번호를 모두 입력해주세요.');
      return;
    }

    try {
      const data = await apiPost('/api/auth/users', newUser);
      setMessage(data.message);
      setShowCreateForm(false);
      setNewUser({ id: '', name: '', password: '', level: '일반' });
      refetchUsers();
    } catch (error) {
      setMessage(error.message || '사용자 생성에 실패했습니다.');
    }
  };

  const handleNameEdit = (userItem) => {
    setEditingName(userItem.id);
    setEditingNameValue(userItem.name);
  };

  const handleNameSave = async (id) => {
    if (!editingNameValue.trim()) {
      setMessage('이름을 입력해주세요.');
      return;
    }

    try {
      const data = await apiPut('/api/auth/users/name', {
        id: id,
        name: editingNameValue
      });
      setMessage(data.message);
      setEditingName(null);
      setEditingNameValue('');
      refetchUsers();
    } catch (error) {
      setMessage(error.message || '이름 수정에 실패했습니다.');
    }
  };

  const handleNameCancel = () => {
    setEditingName(null);
    setEditingNameValue('');
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const data = await apiPut('/api/auth/users/status', {
        id: id,
        status: newStatus
      });
      setMessage(data.message);
      refetchUsers();
    } catch (error) {
      setMessage(error.message || '상태 변경에 실패했습니다.');
    }
  };

  const handleDeleteUser = async (id, name) => {
    const confirmMessage = `정말로 사용자 "${name || id}"를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const data = await apiDelete(`/api/auth/users/${id}`);
      setMessage(data.message);
      refetchUsers();
    } catch (error) {
      setMessage(error.message || '사용자 삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return <LoadingSpinner message="사용자 목록을 불러오는 중..." />;
  }

  if (error) {
    return (
      <div className="admin-panel-container">
        <div className="admin-panel-header">
          <button onClick={() => navigate('/payroll')} className="back-btn" title="뒤로가기">←</button>
          <h2>관리자 메뉴</h2>
        </div>
        <ErrorMessage message={error} type="error" />
        <button onClick={refetchUsers} className="btn btn-primary" style={{ marginTop: '12px' }}>다시 시도</button>
      </div>
    );
  }

  return (
    <div className="admin-panel-container">
      <div className="admin-panel-header">
        <button onClick={() => navigate('/')} className="back-btn" title="뒤로가기">←</button>
        <h2>관리자 메뉴</h2>
      </div>

      {message && (
        <ErrorMessage 
          message={message} 
          type={message.includes('성공') || message.includes('변경') || message.includes('초기화') ? 'success' : 'error'} 
        />
      )}

      {/* 탭 네비게이션 */}
      <div className="admin-tabs" style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '20px', 
        borderBottom: '2px solid #e1e5e9' 
      }}>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'users' ? '3px solid #007bff' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'users' ? '600' : '400',
            color: activeTab === 'users' ? '#007bff' : '#666'
          }}
        >
          사용자 관리
        </button>
        <button
          onClick={() => setActiveTab('permissions')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'permissions' ? '3px solid #007bff' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'permissions' ? '600' : '400',
            color: activeTab === 'permissions' ? '#007bff' : '#666'
          }}
        >
          기업 열람 권한 관리
        </button>
        <button
          onClick={() => setActiveTab('notices')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'notices' ? '3px solid #007bff' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'notices' ? '600' : '400',
            color: activeTab === 'notices' ? '#007bff' : '#666'
          }}
        >
          공지사항 관리
        </button>
        <button
          onClick={() => setActiveTab('payroll-rates')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'payroll-rates' ? '3px solid #007bff' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'payroll-rates' ? '600' : '400',
            color: activeTab === 'payroll-rates' ? '#007bff' : '#666'
          }}
        >
          급여 기준값 관리
        </button>
      </div>

      {/* 사용자 관리 탭 */}
      {activeTab === 'users' && (
      <div className="users-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'nowrap' }}>
          <h3 style={{ margin: 0, whiteSpace: 'nowrap' }}>사용자 관리</h3>
          <button 
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn btn-primary"
            style={{ padding: '6px 12px', fontSize: '14px' }}
            title={showCreateForm ? '취소' : '사용자 생성'}
          >
            {showCreateForm ? '취소' : '+ 생성'}
          </button>
        </div>

        {showCreateForm && (
          <div className="create-user-form" style={{ 
            padding: '20px', 
            border: '1px solid #ddd', 
            borderRadius: '8px', 
            marginBottom: '20px',
            backgroundColor: '#f9f9f9'
          }}>
            <h4>새 사용자 생성</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label>아이디 *</label>
                <input
                  type="text"
                  value={newUser.id}
                  onChange={(e) => setNewUser({ ...newUser, id: e.target.value })}
                  placeholder="아이디 입력"
                  style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                />
              </div>
              <div>
                <label>이름 *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="이름 입력"
                  style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                />
              </div>
              <div>
                <label>비밀번호 *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="비밀번호 입력"
                  style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                />
              </div>
              <div>
                <label>권한</label>
                <select
                  value={newUser.level}
                  onChange={(e) => setNewUser({ ...newUser, level: e.target.value })}
                  style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                >
                  <option value="일반">일반</option>
                  <option value="관리자">관리자</option>
                </select>
              </div>
            </div>
            <button 
              onClick={handleCreateUser}
              className="btn btn-success"
              style={{ padding: '8px 16px' }}
            >
              생성
            </button>
          </div>
        )}

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>사용자명</th>
                <th>이름</th>
                <th>권한</th>
                <th>상태</th>
                <th>생성일</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {(users || []).map((userItem) => (
                <tr key={userItem.id}>
                  <td>{userItem.id}</td>
                  <td>
                    {editingName === userItem.id ? (
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={editingNameValue}
                          onChange={(e) => setEditingNameValue(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleNameSave(userItem.id);
                            if (e.key === 'Escape') handleNameCancel();
                          }}
                          style={{ padding: '4px 8px', width: '150px' }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleNameSave(userItem.id)}
                          className="name-edit-action save"
                          title="저장"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleNameCancel}
                          className="name-edit-action cancel"
                          title="취소"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <span>{userItem.name}</span>
                        <button
                          onClick={() => handleNameEdit(userItem)}
                          disabled={userItem.id === user.id}
                          style={{ 
                            padding: '2px 4px', 
                            fontSize: '12px',
                            opacity: userItem.id === user.id ? 0.5 : 1,
                            cursor: userItem.id === user.id ? 'not-allowed' : 'pointer',
                            background: 'none',
                            border: 'none',
                            minWidth: '24px'
                          }}
                          title={userItem.id === user.id ? '자신의 이름은 수정할 수 없습니다' : '이름 수정'}
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <select
                      value={userItem.level}
                      onChange={(e) => handleLevelChange(userItem.id, e.target.value)}
                      disabled={userItem.id === user.id}
                      className="level-select"
                    >
                      <option value="일반">일반</option>
                      <option value="관리자">관리자</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={userItem.status || 'normal'}
                      onChange={(e) => handleStatusChange(userItem.id, e.target.value)}
                      disabled={userItem.id === user.id}
                      className="status-select"
                      style={{ 
                        padding: '4px 8px',
                        opacity: userItem.id === user.id ? 0.5 : 1,
                        cursor: userItem.id === user.id ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="normal">활성</option>
                      <option value="inactive">비활성</option>
                    </select>
                  </td>
                  <td>
                    {userItem.created_at ? 
                      new Date(userItem.created_at).toLocaleDateString('ko-KR') : 
                      '-'
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleResetPassword(userItem.id)}
                        disabled={userItem.id === user.id}
                        className="reset-password-btn"
                        title="비밀번호를 1234로 초기화"
                      >
                        비밀번호 초기화
                      </button>
                      <button
                        onClick={() => handleDeleteUser(userItem.id, userItem.name)}
                        disabled={userItem.id === user.id}
                        className="delete-user-btn"
                        title={userItem.id === user.id ? '자신의 계정은 삭제할 수 없습니다' : '사용자 삭제'}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* 기업 열람 권한 관리 탭 */}
      {activeTab === 'permissions' && (
        <CompanyPermissionsManager users={users} />
      )}

      {/* 공지사항 관리 탭 */}
      {activeTab === 'notices' && (
        <NoticesManager />
      )}

      {/* 급여 기준값(요율·간이세액표·비과세 한도) 관리 탭 */}
      {activeTab === 'payroll-rates' && (
        <PayrollRatesManager />
      )}

      {/* 관리자 안내는 사용자 관리 탭에서만 표시 */}
      {activeTab === 'users' && (
      <div className="admin-info">
        <h4>관리자 안내</h4>
        <ul>
          <li>새 사용자를 생성할 수 있습니다.</li>
          <li>사용자의 이름을 수정할 수 있습니다.</li>
          <li>사용자의 권한을 변경할 수 있습니다.</li>
          <li>사용자의 상태를 변경할 수 있습니다 (활성/비활성).</li>
          <li>사용자의 비밀번호를 1234로 초기화할 수 있습니다.</li>
          <li>사용자를 삭제할 수 있습니다.</li>
          <li>기업 열람 권한을 관리할 수 있습니다. 기업 이름을 클릭하여 권한을 토글할 수 있습니다.</li>
          <li>자신의 권한, 상태, 비밀번호는 변경할 수 없으며 자신의 계정은 삭제할 수 없습니다.</li>
          <li>변경 사항은 즉시 적용됩니다.</li>
        </ul>
      </div>
      )}
    </div>
  );
}

function CompanyPermissionsManager({ users }) {
  const [companies, setCompanies] = useState([]);
  // 고른 기업은 주소에 둔다(?tab=permissions&company=…)
  const [url, setUrl] = useUrlState();
  const selectedCompany = companies.find((c) => String(c.id) === String(url.get('company'))) || null;
  const setSelectedCompany = (company) => setUrl({ company: company ? company.id : null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      // 관리자용 API 사용 (권한 필터링 없이 모든 기업 조회)
      const data = await apiGet('/api/companies/admin/all');
      setCompanies(data || []);
      setError(null);
    } catch (err) {
      setError(err.message || '기업 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = async (companyId, userId) => {
    try {
      const data = await apiPost('/api/companies/permissions/toggle', {
        company_id: companyId,
        user_id: userId
      });
      setMessage(data.message);
      // 목록 새로고침 후 선택된 기업 정보도 업데이트
      const updatedCompanies = await apiGet('/api/companies/admin/all');
      setCompanies(updatedCompanies || []);
      if (selectedCompany && selectedCompany.id === companyId) {
        const updated = (updatedCompanies || []).find(c => c.id === companyId);
        if (updated) setSelectedCompany(updated);
      }
    } catch (err) {
      setMessage(err.message || '권한 변경에 실패했습니다.');
    }
  };

  const handleCompanyAllVisible = async (companyId) => {
    try {
      const data = await apiPost('/api/companies/permissions/company-all-users', {
        company_id: companyId,
        visible: true
      });
      setMessage(data.message);
      const updatedCompanies = await apiGet('/api/companies/admin/all');
      setCompanies(updatedCompanies || []);
      const updated = (updatedCompanies || []).find(c => c.id === companyId);
      if (updated) setSelectedCompany(updated);
    } catch (err) {
      setMessage(err.message || '권한 설정에 실패했습니다.');
    }
  };

  const handleCompanyAllHidden = async (companyId) => {
    try {
      const data = await apiPost('/api/companies/permissions/company-all-users', {
        company_id: companyId,
        visible: false
      });
      setMessage(data.message);
      const updatedCompanies = await apiGet('/api/companies/admin/all');
      setCompanies(updatedCompanies || []);
      const updated = (updatedCompanies || []).find(c => c.id === companyId);
      if (updated) setSelectedCompany(updated);
    } catch (err) {
      setMessage(err.message || '권한 설정에 실패했습니다.');
    }
  };

  const hasPermission = (company, userId) => {
    if (!company) return false;
    const allowedUsers = company.allowed_users;
    if (allowedUsers === null || allowedUsers === undefined) {
      return true; // 모든 사용자에게 표시
    }
    if (allowedUsers.length === 0) {
      return false; // 빈 리스트면 아무도 못 봄
    }
    return allowedUsers.includes(userId);
  };

  if (loading) {
    return <LoadingSpinner message="기업 목록을 불러오는 중..." />;
  }

  if (error) {
    return <ErrorMessage message={error} type="error" />;
  }

  return (
    <div>
      {message && (
        <ErrorMessage 
          message={message} 
          type={message.includes('성공') || message.includes('변경') || message.includes('설정') || message.includes('부여') ? 'success' : 'error'} 
        />
      )}

      {!selectedCompany ? (
        // 기업 목록 표시
        <div>
          <h3 style={{ marginBottom: '20px' }}>기업 선택</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
            gap: '12px' 
          }}>
            {companies.map(company => (
              <div
                key={company.id}
                onClick={() => setSelectedCompany(company)}
                style={{
                  padding: '16px',
                  border: '2px solid #dee2e6',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#007bff';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,123,255,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#dee2e6';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                  {company.name}
                </div>
                {company.alt_name && company.alt_name !== company.name && (
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    {company.alt_name}
                  </div>
                )}
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {company.allowed_users === null || company.allowed_users === undefined
                    ? '모든 사용자 표시'
                    : company.allowed_users.length === 0
                    ? '모든 사용자 숨김'
                    : `${company.allowed_users.length}명만 표시`}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        // 선택된 기업의 사용자 권한 관리
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <button
                onClick={() => setSelectedCompany(null)}
                style={{
                  padding: '6px 12px',
                  marginRight: '12px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ← 목록으로
              </button>
              <h3 style={{ display: 'inline', margin: 0 }}>
                {selectedCompany.name} - 열람 권한 관리
              </h3>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleCompanyAllVisible(selectedCompany.id)}
                style={{
                  padding: '6px 12px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                title="모든 사용자에게 열람 권한 부여"
              >
                모두 보이게
              </button>
              <button
                onClick={() => handleCompanyAllHidden(selectedCompany.id)}
                style={{
                  padding: '6px 12px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                title="모든 사용자에게 열람 권한 제거"
              >
                모두 안보이게
              </button>
            </div>
          </div>

          <div style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            사용자 이름을 클릭하면 해당 사용자의 열람 권한이 토글됩니다.
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>사용자명</th>
                  <th>이름</th>
                  <th>권한</th>
                  <th>상태</th>
                  <th>열람 권한</th>
                </tr>
              </thead>
              <tbody>
                {users && users.map(user => {
                  const hasAccess = hasPermission(selectedCompany, user.id);
                  return (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.name || user.id}</td>
                      <td>{user.level}</td>
                      <td>
                        <span className={`status-badge ${user.status || 'normal'}`}>
                          {getStatusDisplayName(user.status || 'normal')}
                        </span>
                      </td>
                      <td>
                        <div
                          onClick={() => handleTogglePermission(selectedCompany.id, user.id)}
                          style={{
                            padding: '8px 16px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: hasAccess ? '#d4edda' : '#f8d7da',
                            color: hasAccess ? '#155724' : '#721c24',
                            borderRadius: '4px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.8';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          title={hasAccess ? '권한 있음 (클릭하여 제거)' : '권한 없음 (클릭하여 추가)'}
                        >
                          {hasAccess ? '✓ 권한 있음' : '✗ 권한 없음'}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function NoticesManager() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [newNotice, setNewNotice] = useState({
    title: '',
    content: '',
    is_important: false
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const data = await apiGet('/api/notices/?limit=100');
      setNotices(data || []);
      setError(null);
    } catch (err) {
      setError(err.message || '공지사항 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotice = async () => {
    if (!newNotice.title.trim() || !newNotice.content.trim()) {
      setMessage('제목과 내용을 모두 입력해주세요.');
      return;
    }

    try {
      await apiPost('/api/notices/', newNotice);
      setMessage('공지사항이 생성되었습니다.');
      setShowCreateForm(false);
      setNewNotice({ title: '', content: '', is_important: false });
      fetchNotices();
    } catch (err) {
      setMessage(err.message || '공지사항 생성에 실패했습니다.');
    }
  };

  const handleEditNotice = (notice) => {
    setEditingNotice({
      id: notice.id,
      title: notice.title,
      content: notice.content,
      is_important: notice.is_important
    });
  };

  const handleUpdateNotice = async () => {
    if (!editingNotice.title.trim() || !editingNotice.content.trim()) {
      setMessage('제목과 내용을 모두 입력해주세요.');
      return;
    }

    try {
      await apiPut(`/api/notices/${editingNotice.id}`, {
        title: editingNotice.title,
        content: editingNotice.content,
        is_important: editingNotice.is_important
      });
      setMessage('공지사항이 수정되었습니다.');
      setEditingNotice(null);
      fetchNotices();
    } catch (err) {
      setMessage(err.message || '공지사항 수정에 실패했습니다.');
    }
  };

  const handleDeleteNotice = async (noticeId, title) => {
    if (!window.confirm(`정말로 공지사항 "${title}"를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await apiDelete(`/api/notices/${noticeId}`);
      setMessage('공지사항이 삭제되었습니다.');
      fetchNotices();
    } catch (err) {
      setMessage(err.message || '공지사항 삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return <LoadingSpinner message="공지사항 목록을 불러오는 중..." />;
  }

  if (error) {
    return <ErrorMessage message={error} type="error" />;
  }

  return (
    <div>
      {message && (
        <ErrorMessage 
          message={message} 
          type={message.includes('성공') || message.includes('생성') || message.includes('수정') || message.includes('삭제') ? 'success' : 'error'} 
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>공지사항 관리</h3>
        <button 
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setEditingNotice(null);
          }}
          className="btn btn-primary"
          style={{ padding: '6px 12px', fontSize: '14px' }}
        >
          {showCreateForm ? '취소' : '+ 공지사항 작성'}
        </button>
      </div>

      {showCreateForm && (
        <div style={{ 
          padding: '20px', 
          border: '1px solid #ddd', 
          borderRadius: '8px', 
          marginBottom: '20px',
          backgroundColor: '#f9f9f9'
        }}>
          <h4>새 공지사항 작성</h4>
          <div style={{ marginBottom: '10px' }}>
            <label>제목 *</label>
            <input
              type="text"
              value={newNotice.title}
              onChange={(e) => setNewNotice({ ...newNotice, title: e.target.value })}
              placeholder="공지사항 제목"
              style={{ width: '100%', padding: '8px', marginTop: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label>내용 *</label>
            <textarea
              value={newNotice.content}
              onChange={(e) => setNewNotice({ ...newNotice, content: e.target.value })}
              placeholder="공지사항 내용"
              rows={6}
              style={{ width: '100%', padding: '8px', marginTop: '4px', resize: 'vertical' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={newNotice.is_important}
                onChange={(e) => setNewNotice({ ...newNotice, is_important: e.target.checked })}
                style={{ marginRight: '8px' }}
              />
              중요 공지사항
            </label>
          </div>
          <button 
            onClick={handleCreateNotice}
            className="btn btn-success"
            style={{ padding: '8px 16px' }}
          >
            작성
          </button>
        </div>
      )}

      {editingNotice && (
        <div style={{ 
          padding: '20px', 
          border: '1px solid #007bff', 
          borderRadius: '8px', 
          marginBottom: '20px',
          backgroundColor: '#f0f8ff'
        }}>
          <h4>공지사항 수정</h4>
          <div style={{ marginBottom: '10px' }}>
            <label>제목 *</label>
            <input
              type="text"
              value={editingNotice.title}
              onChange={(e) => setEditingNotice({ ...editingNotice, title: e.target.value })}
              style={{ width: '100%', padding: '8px', marginTop: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label>내용 *</label>
            <textarea
              value={editingNotice.content}
              onChange={(e) => setEditingNotice({ ...editingNotice, content: e.target.value })}
              rows={6}
              style={{ width: '100%', padding: '8px', marginTop: '4px', resize: 'vertical' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={editingNotice.is_important}
                onChange={(e) => setEditingNotice({ ...editingNotice, is_important: e.target.checked })}
                style={{ marginRight: '8px' }}
              />
              중요 공지사항
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={handleUpdateNotice}
              className="btn btn-success"
              style={{ padding: '8px 16px' }}
            >
              저장
            </button>
            <button 
              onClick={() => setEditingNotice(null)}
              className="btn btn-secondary"
              style={{ padding: '8px 16px' }}
            >
              취소
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {notices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            등록된 공지사항이 없습니다.
          </div>
        ) : (
          notices.map(notice => (
            <div
              key={notice.id}
              style={{
                padding: '20px',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                backgroundColor: notice.is_important ? '#fff3cd' : 'white'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0 }}>{notice.title}</h4>
                    {notice.is_important && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#ffc107',
                        color: '#856404',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        중요
                      </span>
                    )}
                  </div>
                  <div style={{ 
                    whiteSpace: 'pre-wrap', 
                    color: '#333',
                    lineHeight: '1.6',
                    marginBottom: '12px'
                  }}>
                    {notice.content}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    작성자: {notice.created_by} | 
                    작성일: {new Date(notice.created_at).toLocaleString('ko-KR')} | 
                    수정일: {new Date(notice.updated_at).toLocaleString('ko-KR')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                  <button
                    onClick={() => handleEditNotice(notice)}
                    className="btn btn-primary"
                    style={{ padding: '6px 12px', fontSize: '14px' }}
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleDeleteNotice(notice.id, notice.title)}
                    className="btn btn-danger"
                    style={{ padding: '6px 12px', fontSize: '14px' }}
                  >
                    삭제
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminPanel;